import api from "./client";
import { parseISODatesAsync } from "@/utils/apiTransformers";
import { mapInChunks, yieldToMain } from "@/utils/yieldToMain";
import { logger } from "@/utils/logger";
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 900;
const POLL_REQUEST_TIMEOUT_MS = 300000;
const START_REQUEST_TIMEOUT_MS = 120000;
function normalizeGorev(gorev) {
    return {
        ...gorev,
        gorev_id: gorev.gorev_id || gorev.blok_id || "",
        arac_id: gorev.arac_id || "",
        arac_tipi: gorev.arac_tipi || "SOLO",
        hat_no: gorev.hat_no || "",
        yon: gorev.yon || "",
        gorev_tipi: gorev.gorev_tipi || "SERVICE",
        baslangic_zamani: gorev.baslangic_zamani || gorev.baslangic || "",
        bitis_zamani: gorev.bitis_zamani || gorev.bitis || "",
        baslangic_lokasyon: gorev.baslangic_lokasyon || "",
        bitis_lokasyon: gorev.bitis_lokasyon || "",
        calisma_suresi: gorev.calisma_suresi || 0,
        baslangic_saniye: gorev.baslangic_saniye ?? null,
        bitis_saniye: gorev.bitis_saniye ?? null,
    };
}
function normalizeDriver(driver) {
    const normalizedDriver = {
        ...driver,
        sofor_id: driver.sofor_id,
        sofor_adi: driver.adi_soyadi || "Bilinmeyen",
        gorevler: [],
    };
    if (driver.bloklar && !driver.gorevler) {
        normalizedDriver.gorevler = driver.bloklar;
    }
    else if (driver.gorevler) {
        normalizedDriver.gorevler = driver.gorevler;
    }
    else {
        normalizedDriver.gorevler = [];
    }
    if (Array.isArray(normalizedDriver.gorevler)) {
        normalizedDriver.gorevler = normalizedDriver.gorevler.map(normalizeGorev);
    }
    return normalizedDriver;
}
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
async function parseEntegrasyonResponseData(data) {
    if (typeof data !== "string") {
        return data;
    }
    await yieldToMain();
    const cleanedJson = data.replace(/:\s*NaN\s*([,}])/g, ": null$1");
    const parsed = JSON.parse(cleanedJson);
    await yieldToMain();
    return parsed;
}
async function normalizeEntegrasyonSonuclar(sonuclar) {
    const normalizedSonuclar = (await parseISODatesAsync(sonuclar));
    if (normalizedSonuclar.drivers && Array.isArray(normalizedSonuclar.drivers)) {
        await yieldToMain();
        normalizedSonuclar.drivers = await mapInChunks(normalizedSonuclar.drivers, normalizeDriver, 25);
    }
    else {
        normalizedSonuclar.drivers = [];
    }
    if (normalizedSonuclar.arac_planlari &&
        Array.isArray(normalizedSonuclar.arac_planlari)) {
        await yieldToMain();
        normalizedSonuclar.arac_planlari = await mapInChunks(normalizedSonuclar.arac_planlari, (arac) => ({
            ...arac,
            arac_id: arac.arac_id || "",
            arac_tipi: arac.arac_tipi || "SOLO",
            calisma_tipi: arac.calisma_tipi ?? null,
            calisma_tipi_adi: arac.calisma_tipi_adi ?? null,
            notlar: Array.isArray(arac.notlar) ? arac.notlar : [],
            gorevler: Array.isArray(arac.gorevler) ? arac.gorevler : [],
        }), 40);
    }
    if (!normalizedSonuclar.stats && normalizedSonuclar.detayli_istatistikler) {
        normalizedSonuclar.stats = buildStatsFromDetayli(normalizedSonuclar.detayli_istatistikler);
    }
    else if (!normalizedSonuclar.stats) {
        normalizedSonuclar.stats = {};
    }
    if (!Array.isArray(normalizedSonuclar.unassigned_services_list)) {
        const legacy = normalizedSonuclar.atanamayan_seferler;
        normalizedSonuclar.unassigned_services_list = Array.isArray(legacy)
            ? legacy
            : [];
    }
    return normalizedSonuclar;
}
function buildStatsFromDetayli(detayli) {
    return {
        totalVehicles: detayli.toplam_arac || 0,
        totalDrivers: detayli.toplam_sofor || 0,
        totalAssignedServices: detayli.atanan_servis_sayisi || 0,
        totalUnassignedServices: detayli.atanmayan_servis_sayisi || 0,
        totalServiceHours: detayli.toplam_servis_suresi || 0,
        totalTravelHours: detayli.toplam_ölü_km_suresi || 0,
        totalIdleHours: detayli.toplam_bosta_bekleme_suresi || 0,
        totalOperationalHours: detayli.toplam_operasyonel_sure || 0,
        totalRestCount: detayli.toplam_mola_sayisi || 0,
        totalWorkHours: detayli.toplam_calisma_suresi || 0,
        totalOvertimeHours: detayli.toplam_fazla_mesai_suresi || 0,
        serviceUtilization: detayli.verimlilik || 0,
        assignmentRate: detayli.atama_orani || 0,
    };
}
const fleetApi = {
    saveFleetPlan(planData) {
        return api.post("/filo-optimizasyon/plan-kaydet", planData).then((res) => res.data);
    },
    getFleetPlans(gunTipi, kategori) {
        const params = {};
        if (gunTipi) {
            params.gun_tipi = gunTipi;
            params.gunTipi = gunTipi;
        }
        if (kategori)
            params.kategori = kategori;
        return api.get("/filo-optimizasyon/planlar", { params }).then((res) => res.data);
    },
    getFleetPlanDetail(planId) {
        return api.get(`/filo-optimizasyon/plan/${planId}`).then((res) => res.data);
    },
    deleteFleetPlan(planId) {
        return api.delete(`/filo-optimizasyon/plan/${planId}`).then((res) => res.data);
    },
    getFleetOptimizationParams() {
        return api.get("/filo-optimizasyon/parametreler").then((res) => res.data);
    },
    /**
     * İptal isteği gönderir. Sunucu thread’i garanti kill etmez.
     */
    stopEntegrasyon(islemId) {
        return api
            .post(`/entegre-optimizasyon/durdur/${islemId}`)
            .then((res) => res.data);
    },
    async pollEntegrasyonDurum(islemId, options) {
        const onProgress = options?.onProgress;
        for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
            if (attempt > 0) {
                await sleep(POLL_INTERVAL_MS);
            }
            const statusRes = await api.get(`/entegre-optimizasyon/durum/${islemId}`, { timeout: POLL_REQUEST_TIMEOUT_MS });
            let statusData;
            try {
                statusData = await parseEntegrasyonResponseData(statusRes.data);
            }
            catch (e) {
                const message = e instanceof Error ? e.message : String(e);
                throw new Error("Durum yanıtı parse edilemedi: " + message);
            }
            if (statusData.message) {
                onProgress?.(statusData.message);
            }
            const durum = statusData.status || "unknown";
            if (durum === "completed") {
                if (!statusData.results) {
                    throw new Error("Optimizasyon tamamlandı ancak sonuç verisi boş");
                }
                onProgress?.("Sonuçlar işleniyor...");
                return normalizeEntegrasyonSonuclar(statusData.results);
            }
            if (durum === "failed" || durum === "not_found") {
                throw new Error(statusData.error ||
                    statusData.message ||
                    "Optimizasyon başarısız oldu");
            }
            if (attempt === 0 || attempt % 20 === 0) {
                logger.debug("Optimizasyon durumu:", { islemId, durum, attempt });
            }
        }
        throw new Error("Optimizasyon zaman aşımına uğradı (45 dakika). İşlem sunucuda sürebilir; sayfayı yenileyip tekrar deneyin.");
    },
    async takasKontrol(body) {
        const res = await api.post("/filo-optimizasyon/takas-kontrol", body);
        return res.data;
    },
    async tasimaOnizle(body) {
        const res = await api.post("/filo-optimizasyon/tasima-onizle", body);
        return res.data;
    },
    async entegrasyonYap(params, options) {
        logger.debug("Entegre optimizasyon isteği gönderiliyor:", params);
        const onProgress = options?.onProgress;
        onProgress?.("Optimizasyon başlatılıyor...");
        const startRes = await api.post("/entegre-optimizasyon/baslat", params, { timeout: START_REQUEST_TIMEOUT_MS, validateStatus: (s) => s === 200 || s === 202 });
        let parsedStart;
        try {
            parsedStart = await parseEntegrasyonResponseData(startRes.data);
        }
        catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            throw new Error("Başlatma yanıtı parse edilemedi: " + message);
        }
        if (!parsedStart.success) {
            throw new Error(parsedStart.error || "Optimizasyon başlatılamadı");
        }
        const islemId = parsedStart.islem_id || parsedStart.id;
        if (parsedStart.async && islemId) {
            options?.onJobStarted?.(String(islemId));
            onProgress?.(parsedStart.message || "Optimizasyon arka planda çalışıyor...");
            return fleetApi.pollEntegrasyonDurum(islemId, options);
        }
        if (parsedStart.results) {
            onProgress?.("Sonuçlar işleniyor...");
            return normalizeEntegrasyonSonuclar(parsedStart.results);
        }
        throw new Error("Geçersiz optimizasyon yanıtı (işlem kimliği veya sonuç yok)");
    },
};
export default fleetApi;
